import { Card } from "@/ingot";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { CartItem, Catalog, Process } from "@/api/types";
import { pickI18nBlob, pickI18nText } from "@/components/admin/I18nField";
import { BulkBar } from "@/components/cart/BulkBar";
import { useCartSelection } from "@/components/cart/CartSelectionContext";
import { OperationIconForProcess } from "@/components/cart/OperationIcon";
import { PartCard } from "@/components/cart/PartCard";
import { PerSectionAddMore } from "@/components/cart/AddMoreUpload";

import { useBusinessInfo } from "@/api/queries/businessInfo";
import { useDisplayMoney } from "@/lib/currency";
import { applyVatDisplay, storefrontVatDisplay, vatSuffixVariant } from "@/lib/vatDisplay";
import { getOperationTone, operationToneColor } from "@/lib/operationTone";
import type { FrontendModule } from "@/processes/types";

/**
 * Collapsible per-process section in the cart.
 *
 * Groups all ``CartItem``s whose primary operation matches
 * ``processSlug``. Renders an icon-driven header (icon from PR1's
 * platform-managed asset, slug→tone from ``operationTone.ts``), a
 * subtotal across all variants in this section, and an expandable
 * body containing one ``PartCard`` per item plus a per-section
 * ``BulkBar`` that flips on once the user checks a variant.
 */
export function OperationSection({
  processSlug,
  process,
  items,
  catalog,
  locale,
  lang,
  moduleMap,
  onDeleteItem,
  onDeleteLine,
  removeItemLabel,
  removeLineLabel,
  editLineLabel,
  itemHref,
  maxExtraVariantsPerItem,
  unassignedFallbackLabel,
  processLabel,
  currencyCode,
}: {
  processSlug: string;
  process: Process | null;
  items: CartItem[];
  catalog: Catalog | undefined;
  locale: string;
  lang: string;
  moduleMap: Map<string, FrontendModule>;
  onDeleteItem: (itemSlug: string) => void;
  onDeleteLine: (lineId: number) => void;
  removeItemLabel: string;
  removeLineLabel: string;
  editLineLabel: string;
  itemHref: (itemSlug: string) => string;
  maxExtraVariantsPerItem: number | null;
  unassignedFallbackLabel: string;
  /** KAN-1027 — popisek libovolného procesu pro rozpad ceny u řádku.
   *  Sekce zná jen svůj vlastní ``process``; navazující operace patří
   *  jinému, a její jméno musí přijít odsud. */
  processLabel?: (processType: string) => string;
  /** Účetní měna košíku — předává se dál do rozpadu ceny u varianty. */
  currencyCode?: string | null;
}): JSX.Element {
  const { t } = useTranslation("cart");
  const { format } = useDisplayMoney(locale);
  // KAN-1432 — souhrn sekce nemá přepínač, tak jde podle toho, komu tenant
  // prodává; bez sazby DPH zůstává „bez DPH". KAN-1498 — a neplátci
  // odpadá i to: žádnou daň neúčtuje, takže doložka nemá co říkat.
  const vat = storefrontVatDisplay(useBusinessInfo().data);
  const toneColor = operationToneColor(process);
  const tone = getOperationTone(toneColor);
  const [expanded, setExpanded] = useState(true);
  const { setMany, countInOperation } = useCartSelection();
  const checkboxRef = useRef<HTMLInputElement>(null);

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + item.lines.reduce((s, line) => s + (line.line_total_czk ?? 0), 0),
        0,
      ),
    [items],
  );

  const totalQty = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + item.lines.reduce((s, l) => s + l.quantity, 0),
        0,
      ),
    [items],
  );

  const totalVariants = useMemo(
    () => items.reduce((sum, item) => sum + item.lines.length, 0),
    [items],
  );

  // Pass {id, material_code} per line so BulkBar can intersect
  // axis values against actually-selected materials (PR5).
  // Aggregate warning count across all items in this section so the
  // header can render a single warn pill. ``warnings_count`` ships from
  // BE in PR11; older payloads default to 0 (no pill).
  const totalWarnings = useMemo(
    () => items.reduce((n, item) => n + (item.warnings_count ?? 0), 0),
    [items],
  );

  // Per-process properties hint for the section subtitle — derived
  // from the FrontendModule's ``cartColumnHeaders`` (already i18n keys),
  // joined with " · ". Operators see e.g. "Vlastnosti: Materiál ·
  // Tloušťka" so they know what each variant row exposes.
  const propertiesHint = useMemo(() => {
    const m = moduleMap.get(processSlug);
    // What the customer configures, named by the operation's own fields
    // (KAN-1432) — the fixed headers only name the two universal columns.
    const rawFields = (m?.configurator_schema as { fields?: unknown } | undefined)?.fields;
    const fields = (Array.isArray(rawFields) ? rawFields : []) as Array<Record<string, unknown>>;
    const named = fields
      .filter((f) => f && f.kind !== "visualization" && f.kind !== "file")
      .map((f) =>
        pickI18nText(
          typeof f.label_cs === "string" ? f.label_cs : "",
          typeof f.label_en === "string" ? f.label_en : "",
          lang,
        ),
      )
      .filter(Boolean);
    if (named.length > 0) return named.join(" · ");
    const headers = m?.cartColumnHeaders ?? [];
    if (headers.length === 0) return null;
    return headers.map((key) => t(key, { defaultValue: key })).join(" · ");
  }, [moduleMap, processSlug, t, lang]);

  // Bulk-apply dropdowns the per-operation BulkBar should render.
  // Comes from the module registry; modules without a declaration get
  // an empty list (Delete-only bulk bar).

  const opLines = useMemo(
    () =>
      items.flatMap((item) =>
        item.lines.map((line) => ({
          id: line.id,
          material_code: line.material_code ?? null,
        })),
      ),
    [items],
  );

  const opLineIds = useMemo(() => opLines.map((l) => l.id), [opLines]);
  const selectedCount = countInOperation(opLineIds);
  const allChecked = opLineIds.length > 0 && selectedCount === opLineIds.length;
  const someChecked = selectedCount > 0 && selectedCount < opLineIds.length;
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someChecked;
  }, [someChecked]);

  const displayName =
    process?.display_name ?? (processSlug === "unassigned" ? unassignedFallbackLabel : processSlug);

  return (
    <Card
      padded={false}
      className="overflow-hidden"
      style={{ borderLeft: `3px solid ${tone.borderLeft}` }}
      data-testid={`cart-operation-section-${processSlug}`}
    >
      {selectedCount > 0 ? (
        <BulkBar
          opLines={opLines}
          processSlug={processSlug}
          toneColor={toneColor}
        />
      ) : (
      <header
        className="flex min-h-[4.25rem] cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-bg"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={`cart-op-body-${processSlug}`}
        data-testid={`cart-operation-section-${processSlug}-toggle`}
      >
        <div className="flex min-w-0 items-center gap-3">
          {opLineIds.length > 0 && (
            <label
              className="flex shrink-0 cursor-pointer items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={checkboxRef}
                type="checkbox"
                checked={allChecked}
                onChange={(e) => setMany(opLineIds, e.target.checked, processSlug)}
                style={{ accentColor: tone.dot }}
                className="h-4 w-4 cursor-pointer"
                data-testid={`cart-operation-section-${processSlug}-select-all`}
                aria-label={t("operationSection.selectAllAria", {
                  defaultValue: "Vybrat všechny díly v této operaci",
                })}
              />
            </label>
          )}
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{
              background: tone.bg,
              border: `1px solid ${tone.border}`,
              color: tone.ink,
            }}
          >
            <OperationIconForProcess process={process} slug={processSlug} size={22} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-ink">{displayName}</h2>
              <span
                className="inline-flex h-5 items-center gap-1 rounded-md border px-2 text-[11px] font-medium"
                style={{
                  background: tone.bg,
                  color: tone.ink,
                  borderColor: tone.border,
                }}
                data-testid={`cart-operation-section-${processSlug}-files-pill`}
              >
                {items.length === 0
                  ? t("operationSection.empty", { defaultValue: "prázdné" })
                  : t("operationSection.filesPill", {
                      defaultValue: "{{count}} souborů",
                      count: items.length,
                    })}
              </span>
              {totalVariants > items.length && (
                <span className="mono inline-flex h-5 items-center rounded-md border border-border bg-surface px-2 text-[11px] font-medium text-ink-2">
                  {t("operationSection.variantsTotalPill", {
                    defaultValue: "{{count}} variant celkem",
                    count: totalVariants,
                  })}
                </span>
              )}
              {totalQty > 0 && (
                <span className="mono inline-flex h-5 items-center rounded-md border border-border bg-surface px-2 text-[11px] font-medium text-ink-2">
                  {t("operationSection.qtyPill", {
                    defaultValue: "{{count}} ks",
                    count: totalQty,
                  })}
                </span>
              )}
              {totalWarnings > 0 && (
                <span
                  className="inline-flex h-5 items-center gap-1 rounded-md border border-warn-border bg-warn-bg px-2 text-[11px] font-medium text-warn"
                  data-testid={`cart-operation-section-${processSlug}-warnings`}
                  title={t("operationSection.warningsPillTitle", {
                    defaultValue: "Některé díly potřebují pozornost",
                  })}
                >
                  <span aria-hidden="true">⚠</span>
                  {t("operationSection.warningsPill", {
                    defaultValue: "{{count}} upozornění",
                    count: totalWarnings,
                  })}
                </span>
              )}
            </div>
            {(propertiesHint || process?.operation_category_label) && (
              <p
                className="mt-1 text-[11px] text-ink-3"
                data-testid={`cart-operation-section-${processSlug}-properties`}
              >
                {process?.operation_category_label && (
                  <>
                    <span className="text-ink-2">
                      {pickI18nBlob(
                        process.operation_category_label_i18n,
                        process.operation_category_label,
                        process.operation_category_label_en,
                        lang,
                      )}
                    </span>
                    {propertiesHint ? " · " : ""}
                  </>
                )}
                {propertiesHint && (
                  <>
                    {t("operationSection.propertiesPrefix", {
                      defaultValue: "Vlastnosti:",
                    })}{" "}
                    <span className="mono text-ink-2">{propertiesHint}</span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex flex-col items-end">
            <span
              className="mono text-base font-semibold tabular-nums text-ink"
              data-testid={`cart-operation-section-${processSlug}-subtotal`}
            >
              {format(applyVatDisplay(subtotal, vat))}
            </span>
            {vat.appliesVat && (
              <span className="mono text-[10px] uppercase tracking-[0.08em] text-ink-4">
                {t(`operationSection.subtotalCaption${vatSuffixVariant(vat)}`)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-ink-2 transition-transform hover:bg-bg"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
            aria-label={
              expanded
                ? t("operationSection.collapse", { defaultValue: "Sbalit" })
                : t("operationSection.expand", { defaultValue: "Rozbalit" })
            }
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>
      )}

      {expanded && (
        <div id={`cart-op-body-${processSlug}`} className="border-t border-border">
          <div className="space-y-3 bg-bg p-3">
            {items.map((item) => (
              <PartCard
                key={item.id}
                item={item}
                catalog={catalog}
                locale={locale}
                moduleMap={moduleMap}
                onDeleteItem={() => onDeleteItem(item.id)}
                onDeleteLine={onDeleteLine}
                removeItemLabel={removeItemLabel}
                removeLineLabel={removeLineLabel}
                editLineLabel={editLineLabel}
                maxExtraVariantsPerItem={maxExtraVariantsPerItem}
                previewHref={itemHref(item.id)}
                nameHref={itemHref(item.id)}
                addConfigHref={itemHref(item.id)}
                processSlug={processSlug}
                processLabel={processLabel}
                currencyCode={currencyCode}
                netPricesLabelled={vat.appliesVat}
              />
            ))}
            {process && process.is_available && process.requires_file && (
              <PerSectionAddMore process={process} lang={lang} tone={tone} />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
